/**
 * The reader's theme choice.
 *
 * Three states, not two. "System" is the default and is the *absence* of a stamp on `<html>`, which
 * is what lets the media query in index.css decide. An explicit choice stamps `data-theme`, and the
 * `:not([data-theme='light'])` guard on the dark block is what makes "light on a dark OS" work as
 * well as "dark on a light OS".
 *
 * Storage is wrapped because `localStorage` throws outright — it does not return null — in a browser
 * with site data blocked, and a colour preference is not worth a blank page.
 */

export type ThemeChoice = 'system' | 'light' | 'dark'

export const THEME_KEY = 'dota-oracle:theme'

const isChoice = (value: unknown): value is ThemeChoice =>
  value === 'system' || value === 'light' || value === 'dark'

export function readTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    return isChoice(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

/**
 * Apply a choice to the document.
 *
 * Exported and used by the pre-paint script in index.html as well as by the toggle, so there is one
 * definition of what a choice means to the DOM rather than two that can drift.
 */
export function applyTheme(choice: ThemeChoice) {
  const root = document.documentElement
  if (choice === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', choice)
  }
}

export function writeTheme(choice: ThemeChoice) {
  applyTheme(choice)
  try {
    window.localStorage.setItem(THEME_KEY, choice)
  } catch {
    // A preference we cannot persist still applies for this visit.
  }
}

/** What the reader is actually looking at right now, which "system" alone does not tell you. */
export function resolvedTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}
